import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Language, DEFAULT_LANGUAGE, getTranslation, translateText as translateTextFn, SUPPORTED_LANGUAGES } from '../i18n'
import { getAssetDisplaySymbol as getDisplaySymbol, resolveFuturesApiSymbol } from '../config/futuresDisplay'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
  translateText: (text: string) => string
  getAssetName: (symbol: string, name: string, type?: string) => string
  getAssetDisplaySymbol: (symbol: string, type?: string) => string
  resolveFuturesApiSymbol: (symbol: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE)

  // 从localStorage加载语言设置
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') as Language
    if (savedLanguage === 'zh') {
      setLanguageState('en')
      localStorage.setItem('language', 'en')
      return
    }
    if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
      setLanguageState(savedLanguage)
    }
  }, [])

  // 设置语言并保存到localStorage
  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem('language', lang)
  }

  // 翻译函数
  const t = (key: string): string => {
    return getTranslation(language, key)
  }

  const translateText = (text: string): string => {
    return translateTextFn(language, text)
  }

  const getAssetName = (symbol: string, name: string, type?: string): string => {
    const key = `futures.${symbol}`
    const localized = getTranslation(language, key)
    if (type === 'futures' || localized !== key) {
      if (localized !== key) return localized
    }
    return translateText(name)
  }

  const getAssetDisplaySymbol = (symbol: string, type?: string): string => {
    return getDisplaySymbol(symbol, type)
  }

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    translateText,
    getAssetName,
    getAssetDisplaySymbol,
    resolveFuturesApiSymbol
  }

  return (
    <LanguageContext.Provider value={value}>
      <div key={language}>
        {children}
      </div>
    </LanguageContext.Provider>
  )
}

// 自定义Hook
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export default LanguageContext